import { onBeforeUnmount, Ref } from 'vue'
import { jsPDF } from 'jspdf'

import { WidgetExtended } from '@/types/dashboard'
import bus from '@/event-bus/EventBus'
import type { ComponentExposed } from 'vue-component-type-helpers'
import VChart from 'vue-echarts'

/* Types */
type DownloadType = 'image/jpeg' | 'application/pdf' | 'image/png';
type ImageType = 'png' | 'jpeg';
type DownloadChartParams = {
    type: DownloadType;
    WidgetID: number;
};

export default function useChartActions (
    widget: WidgetExtended,
    chartRef: Ref<ComponentExposed<typeof VChart> | undefined>
) {
    /* Helper Functions */
    function isCurrentWidget (widgetID: number): boolean {
        return widgetID === widget.WidgetID
    }

    function getImageType (type: DownloadType): ImageType | undefined {
        switch (type) {
            case 'image/png':
                return 'png'
            case 'image/jpeg':
                return 'jpeg'
            default:
                return undefined
        }
    }

    function getDataURL (type: ImageType): string | undefined {
        const chartInstance = chartRef.value?.chart

        if (!chartInstance) {
            console.error('Failed to get ECharts instance')
            return
        }

        return chartInstance.getDataURL({
            type,
            pixelRatio: 2,
            backgroundColor: '#fff',
        })
    }

    function getFileName (widget: WidgetExtended, extension: string) {
        return `${widget.Title}-${widget.WidgetID}.${extension}`
    }

    /* Methods */
    function printChart (widgetID: number): void {
        if (!isCurrentWidget(widgetID)) {
            return
        }

        const dataURL = getDataURL('png')

        if (!dataURL) {
            return
        }

        const newWin = window.open('', '_blank')
        if (newWin) {
            newWin.document.write(`
        <html>
          <head>
            <title>Print Chart</title>
            <style>
              body { margin: 0; padding: 0; text-align: center; }
              img { max-width: 100%; }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <img src="${dataURL}" alt="Chart Image" />
          </body>
        </html>
      `)
            newWin.document.close()
        } else {
            console.error('Failed to open new window for printing')
        }
    }

    function downloadChart ({ type, WidgetID }: DownloadChartParams): void {
        if (!isCurrentWidget(WidgetID)) {
            return
        }

        const imageType = getImageType(type)

        if (!imageType && type !== 'application/pdf') {
            console.error('Unsupported download type')
            return
        }

        if (type === 'application/pdf') {
            const dataURL = getDataURL('png')

            if (!dataURL) {
                return
            }

            // Create a PDF and add the image
            const pdf = new jsPDF()
            const imgProps = pdf.getImageProperties(dataURL)
            const pdfWidth = pdf.internal.pageSize.getWidth()
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width

            pdf.addImage(dataURL, 'PNG', 0, 0, pdfWidth, pdfHeight)
            pdf.save(getFileName(widget, 'pdf'))
        } else if (imageType) {
            const dataURL = getDataURL(imageType)

            if (!dataURL) {
                return
            }

            // Create a link element and trigger the download
            const link = document.createElement('a')
            link.href = dataURL
            link.download = getFileName(widget, imageType)
            link.click()
        }
    }

    /* Event Listeners */
    bus.on('print-chart', printChart)
    bus.on('download-chart', downloadChart)

    /* Cleanup */
    onBeforeUnmount(() => {
        bus.off('print-chart', printChart)
        bus.off('download-chart', downloadChart)
    })
}
